import React, { useCallback, useEffect, useMemo, useState } from 'react'
import log from 'loglevel'

import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import {
  ChatContext,
  ChatContextSelection,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { FlexAlign, FlexColumn, FlexRow } from 'src/components/Reusable/Flex'
import { FileSelector } from 'src/components/Reusable/Forms/FileSelector/FileSelector'
import { DeleteSVG, UploadSVG } from 'src/components/Icons/Basic'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import { Spinner } from 'src/Throbber/Spinner'
import { TooltipIconButton } from 'src/components/Reusable/Buttons/TooltipIconButton'
import {
  StandardButton,
  StandardButtonVariant,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { useConfirmationModal } from 'src/components/ModalForms/useConfirmationModal'
import { getMimeTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { useIsGlobalContentAccount } from 'src/context/Gatekeeper'
import { MultiSelectModal } from 'src/components/Reusable/MultiSelectModal'
import { ContextSelectorTableText } from '../chatContextPanel.styles'
import {
  useDeleteDocument,
  useGetDocumentGroup,
  useGetSupportKbDocumentGroup,
  useUploadDocument,
} from './documentGroup.hooks'
import { DocumentDeleteButton } from './documentGroup.styles'

const DOCUMENT_GROUP_REFRESH_INTERVAL = 1000 * 15 // 5 seconds

export type DocumentGroupModalProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

export const DocumentGroupModal = ({
  show,
  hide,
  onBack,
  context,
  setContext,
}: DocumentGroupModalProps): JSX.Element => {
  const currentSelection = useMemo(() => {
    return context[ChatContextSourceType.DOCUMENT_GROUP]
  }, [context])

  const [showUploadModal, setShowUploadModal] = useState(false)

  const { data, isLoading, error, mutate } = useGetDocumentGroup()
  const deleteDocument = useDeleteDocument()

  useEffect(() => {
    const interval = setInterval(() => {
      mutate()
    }, DOCUMENT_GROUP_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [mutate])

  const onDeleteDocument = useCallback(
    async (documentId: string) => {
      try {
        await deleteDocument({ documentId })
        mutate()
      } catch (error) {
        log.error('Failed to delete document', error)
      }
    },
    [deleteDocument, mutate],
  )

  const items = useMemo(() => {
    return (
      data?.getDocumentsList()?.map((document) => {
        const Icon = getMimeTypeIcon(document.getMimeType())

        return {
          id: document.getId(),
          renderItems: [
            <Icon width="24px" height="24px" key="0-icon" />,
            <ContextSelectorTableText key="1-title">
              {document.getFileName()}
            </ContextSelectorTableText>,
            document.getIsBeingIndexed() ? (
              <FlexRow gap={10} align={FlexAlign.CENTER} key="2-processing">
                <Spinner color="secondary" size="xs" />
                <ContextSelectorTableText>
                  Processing...
                </ContextSelectorTableText>
              </FlexRow>
            ) : null,
            <DeleteDocumentComponent
              documentId={document.getId()}
              documentName={document.getFileName()}
              deleteDocument={onDeleteDocument}
              key="3-delete"
            />,
          ],
        }
      }) ?? []
    )
  }, [data, onDeleteDocument])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.DOCUMENT_GROUP]: newSelection,
      })
    },
    [context, setContext],
  )

  const onUploadSuccess = useCallback(() => {
    setShowUploadModal(false)
    mutate()
  }, [mutate])

  const uploadDocumentButton = useMemo(() => {
    return (
      <StandardButton
        variant={StandardButtonVariant.SECONDARY}
        buttonSize={StandardButtonSize.MEDIUM}
        onClick={() => void setShowUploadModal(true)}
      >
        <UploadSVG width="16px" height="16px" />
        Upload New Document
      </StandardButton>
    )
  }, [])

  const isGlobalContentAccount = useIsGlobalContentAccount()
  const [showSupportKbModal, setShowSupportKbModal] = useState(false)

  const manageSupportKbButton = useMemo(() => {
    if (!isGlobalContentAccount) return undefined
    return (
      <StandardButton
        variant={StandardButtonVariant.SECONDARY}
        buttonSize={StandardButtonSize.MEDIUM}
        onClick={() => void setShowSupportKbModal(true)}
      >
        Manage Support KB
      </StandardButton>
    )
  }, [isGlobalContentAccount])

  return (
    <>
      <MultiSelectModal
        title="Select Knowledge Base Documents"
        description="The AI agent will only consider the documents you select here during your conversation."
        itemType="document"
        currentSelection={currentSelection}
        onCommit={onCommit}
        show={show && !showSupportKbModal}
        hide={hide}
        onBack={onBack}
        isLoading={isLoading}
        items={items}
        numberOfColumns={4}
        error={error}
        customActionButton={
          <FlexRow gap={8}>
            {manageSupportKbButton}
            {uploadDocumentButton}
          </FlexRow>
        }
      />
      {showSupportKbModal && (
        <SupportKbDocumentGroupModal
          onBack={() => setShowSupportKbModal(false)}
        />
      )}
      {showUploadModal && (
        <UploadDocumentModal
          onCancel={() => setShowUploadModal(false)}
          onSucess={onUploadSuccess}
        />
      )}
    </>
  )
}

const SUPPORT_KB_GROUP_NAME = 'Support KB'

type SupportKbDocumentGroupModalProps = {
  onBack: () => void
}

const SupportKbDocumentGroupModal = ({
  onBack,
}: SupportKbDocumentGroupModalProps): JSX.Element => {
  const [showUploadModal, setShowUploadModal] = useState(false)

  const { data, isLoading, mutate } = useGetSupportKbDocumentGroup()
  const deleteDocument = useDeleteDocument()

  useEffect(() => {
    const interval = setInterval(() => {
      mutate()
    }, DOCUMENT_GROUP_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [mutate])

  const onDeleteDocument = useCallback(
    async (documentId: string) => {
      try {
        await deleteDocument({ documentId })
        mutate()
      } catch (error) {
        log.error('Failed to delete Support KB document', error)
      }
    },
    [deleteDocument, mutate],
  )

  const items = useMemo(() => {
    return (
      data?.getDocumentsList()?.map((document) => {
        const Icon = getMimeTypeIcon(document.getMimeType())

        return {
          id: document.getId(),
          renderItems: [
            <Icon width="24px" height="24px" key="0-icon" />,
            <ContextSelectorTableText key="1-title">
              {document.getFileName()}
            </ContextSelectorTableText>,
            document.getIsBeingIndexed() ? (
              <FlexRow gap={10} align={FlexAlign.CENTER} key="2-processing">
                <Spinner color="secondary" size="xs" />
                <ContextSelectorTableText>
                  Processing...
                </ContextSelectorTableText>
              </FlexRow>
            ) : null,
            <DeleteDocumentComponent
              documentId={document.getId()}
              documentName={document.getFileName()}
              deleteDocument={onDeleteDocument}
              key="3-delete"
            />,
          ],
        }
      }) ?? []
    )
  }, [data, onDeleteDocument])

  const onUploadSuccess = useCallback(() => {
    setShowUploadModal(false)
    mutate()
  }, [mutate])

  const uploadButton = useMemo(() => {
    return (
      <StandardButton
        variant={StandardButtonVariant.SECONDARY}
        buttonSize={StandardButtonSize.MEDIUM}
        onClick={() => void setShowUploadModal(true)}
      >
        <UploadSVG width="16px" height="16px" />
        Upload New Document
      </StandardButton>
    )
  }, [])

  return (
    <>
      <MultiSelectModal
        title={SUPPORT_KB_GROUP_NAME}
        description="Manage documents in the Support KB group. These are used to answer product help questions."
        itemType="document"
        currentSelection={{ includeAll: false }}
        onCommit={() => undefined}
        show={true}
        hide={onBack}
        onBack={onBack}
        isLoading={isLoading}
        items={items}
        numberOfColumns={4}
        customActionButton={uploadButton}
      />
      {showUploadModal && (
        <UploadDocumentModal
          groupName={SUPPORT_KB_GROUP_NAME}
          onCancel={() => setShowUploadModal(false)}
          onSucess={onUploadSuccess}
        />
      )}
    </>
  )
}

type UploadDocumentModalProps = {
  onCancel: () => void
  onSucess: () => void
  groupName?: string
}

const UploadDocumentModal = ({
  onCancel,
  onSucess,
  groupName,
}: UploadDocumentModalProps): JSX.Element => {
  const [files, setFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const uploadDocument = useUploadDocument()

  const handleFileUpload = useCallback((files: File[]) => {
    setFiles(files)
    setErrors([])
  }, [])

  const clearData = useCallback(() => {
    setFiles([])
    setErrors([])
  }, [])

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setIsSubmitting(true)
      try {
        const uploads = files.map((file) => uploadDocument({ file, groupName }))
        const results = await Promise.allSettled(uploads)
        const errors = results.filter(
          (result) => result.status === 'rejected',
        ) as PromiseRejectedResult[]
        if (errors.length > 0) {
          const errorMessages = errors
            .map((error) =>
              error.reason instanceof Error ? error.reason.message : '',
            )
            .filter((msg) => msg)
          setErrors(
            errorMessages.length > 0 ? errorMessages : ['An error occurred'],
          )
        } else {
          clearData()
          onSucess()
        }
      } catch (error) {
        setErrors([
          error instanceof Error ? error.message : 'An error occurred',
        ])
      } finally {
        setIsSubmitting(false)
      }
    },
    [onSucess, uploadDocument, files, clearData, groupName],
  )

  return (
    <ModalForm
      show={true}
      hide={onCancel}
      title="Upload New Documents"
      submitText="Upload"
      formId={ModalFormId.UPLOAD_DOCUMENT}
      size="sm"
    >
      <form id={ModalFormId.UPLOAD_DOCUMENT} onSubmit={onSubmit}>
        <FileSelector
          files={files}
          text="Click or drag and drop documents here"
          onChange={handleFileUpload}
          onClear={clearData}
          isLoading={isSubmitting}
          isValid={files.length > 0}
          allowMultiple={true}
          errorMessage=""
        />
        <FlexColumn gap={10}>
          {errors.map((error, index) => (
            <StyledInputError key={index} showError={!!error}>
              {error}
            </StyledInputError>
          ))}
        </FlexColumn>
      </form>
    </ModalForm>
  )
}

type DeleteDocumentComponentProps = DeleteDocumentModalProps

const DeleteDocumentComponent = ({
  documentId,
  documentName,
  deleteDocument,
}: DeleteDocumentComponentProps): JSX.Element => {
  const onDeleteDocument = useDeleteDocumentModal({
    documentId,
    documentName,
    deleteDocument,
  })

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onDeleteDocument()
    },
    [onDeleteDocument],
  )

  return (
    <FlexRow align={FlexAlign.FLEX_END} gap={10}>
      <DocumentDeleteButton
        variant={StandardButtonVariant.SECONDARY}
        buttonSize={StandardButtonSize.MEDIUM}
      >
        <TooltipIconButton
          tooltipText="Delete Document"
          icon={<DeleteSVG />}
          onClick={onClick}
        />
      </DocumentDeleteButton>
    </FlexRow>
  )
}

type DeleteDocumentModalProps = {
  documentId: string
  documentName: string
  deleteDocument: (documentId: string) => void
}

const useDeleteDocumentModal = ({
  documentId,
  documentName,
  deleteDocument,
}: DeleteDocumentModalProps): (() => void) => {
  const onConfirmCB = useCallback(async () => {
    try {
      if (documentId) {
        await deleteDocument(documentId)
      }
    } catch (err) {
      log.error('Error while deleting document:', err)
    }
  }, [documentId, deleteDocument])

  return useConfirmationModal({
    title: 'Delete Document',
    body: (
      <FlexColumn gap={10} align={FlexAlign.FLEX_START}>
        <p style={{ textWrap: 'balance' }}>
          The document <i style={{ wordBreak: 'break-all' }}>{documentName}</i>{' '}
          will be deleted from your organization. This action cannot be undone.
        </p>
      </FlexColumn>
    ),
    confirmText: 'Delete Document',
    onConfirmCB,
    throbber: {
      color: 'primary',
      size: 'xl',
    },
  })
}
